import React, { useState, useEffect, useRef, useCallback } from 'react';
import TouchButton from '../ui/TouchButton';
import { TouchInput } from '../ui/TouchForm';
import searchService from '../../services/SearchService';

const SearchBox = ({ 
  onSearch, 
  onLocationSelect, 
  placeholder = "Search locations, neighborhoods, cities...", 
  showFilters = true,
  autoFocus = false,
  className = ''
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const inputRef = useRef(null);
  const suggestionRefs = useRef([]);
  const debounceRef = useRef(null);

  // Load search history on mount
  useEffect(() => {
    loadSearchHistory();
  }, []);

  // Auto focus if requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const loadSearchHistory = async () => {
    try {
      const history = await searchService.getSearchHistory();
      setSearchHistory(history.history || []);
    } catch (error) {
      console.error('Failed to load search history:', error);
    }
  };

  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const result = await searchService.getAutocompleteSuggestions(searchQuery, {
        limit: 8
      });
      setSuggestions(result.suggestions || []);
    } catch (error) {
      console.error('Autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length >= 2) {
      setShowSuggestions(true);
      setShowHistory(false);
      
      // Debounce API calls
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 300);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
      if (value.length === 0 && searchHistory.length > 0) {
        setShowHistory(true);
      }
    }
  };

  const handleInputFocus = () => {
    if (query.length >= 2) {
      setShowSuggestions(true);
    } else if (searchHistory.length > 0) {
      setShowHistory(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow clicks on suggestions
    setTimeout(() => {
      setShowSuggestions(false);
      setShowHistory(false);
    }, 150);
  };

  const handleKeyDown = (e) => {
    const itemsCount = showHistory ? searchHistory.length : suggestions.length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % itemsCount);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev <= 0 ? itemsCount - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (showHistory) {
            handleHistorySelect(searchHistory[selectedIndex]);
          } else {
            handleSuggestionSelect(suggestions[selectedIndex]);
          }
        } else if (query.trim()) {
          handleSearch(query);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setShowHistory(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSuggestionSelect = async (suggestion) => {
    setQuery(suggestion.text);
    setShowSuggestions(false);
    setShowHistory(false);
    setSelectedIndex(-1);

    // Save to search history
    await searchService.saveSearchToHistory(
      suggestion.text, 
      suggestion.type, 
      { subtitle: suggestion.subtitle }
    );

    if (suggestion.type === 'location' && suggestion.id) {
      if (onLocationSelect) {
        onLocationSelect({
          id: suggestion.id,
          name: suggestion.text,
          type: suggestion.type
        });
      }
    } else {
      handleSearch(suggestion.text);
    }
  };

  const handleHistorySelect = (historyItem) => {
    setQuery(historyItem.searchTerm);
    setShowHistory(false);
    setSelectedIndex(-1);
    handleSearch(historyItem.searchTerm);
  };

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;

    // Save to search history
    await searchService.saveSearchToHistory(searchQuery, 'location');

    // Reload history
    loadSearchHistory();

    if (onSearch) {
      onSearch(searchQuery);
    }

    setShowSuggestions(false);
    setShowHistory(false);
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setShowHistory(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const clearHistory = async () => {
    try {
      await searchService.clearSearchHistory();
      setSearchHistory([]);
      setShowHistory(false);
    } catch (error) {
      console.error('Failed to clear search history:', error);
    }
  };

  return (
    <div className={`search-box ${className}`} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <TouchInput
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            paddingRight: query ? '80px' : '40px',
            paddingLeft: '40px'
          }}
        />
        
        {/* Search Icon */}
        <div style={{
          position: 'absolute',
          left: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#666',
          fontSize: '16px'
        }}>
          🔍
        </div>

        {/* Loading Spinner */}
        {isLoading && (
          <div style={{
            position: 'absolute',
            right: query ? '50px' : '12px',
            top: '50%',
            transform: 'translateY(-50%)'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid #e0e0e0',
              borderTop: '2px solid #2196F3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        )}

        {/* Clear Button */}
        {query && (
          <TouchButton
            onClick={clearSearch}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '28px',
              height: '28px',
              padding: '0',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '50%',
              color: '#666',
              fontSize: '18px'
            }}
          >
            ×
          </TouchButton>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {(showSuggestions || showHistory) && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #e0e0e0'
        }}>
          {showHistory && searchHistory.length > 0 && (
            <>
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                fontSize: '12px',
                color: '#666',
                fontWeight: '600',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>Recent Searches</span>
                <TouchButton
                  onClick={clearHistory}
                  variant="ghost"
                  size="small"
                  style={{
                    fontSize: '12px',
                    padding: '4px 8px',
                    height: 'auto',
                    minHeight: 'auto'
                  }}
                >
                  Clear
                </TouchButton>
              </div>
              {searchHistory.slice(0, 5).map((item, index) => (
                <div
                  key={index}
                  ref={el => suggestionRefs.current[index] = el}
                  onClick={() => handleHistorySelect(item)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: index < searchHistory.length - 1 ? '1px solid #f8f9fa' : 'none',
                    backgroundColor: selectedIndex === index ? '#f8f9fa' : 'transparent',
                    transition: 'background-color 150ms ease'
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ color: '#666', fontSize: '16px' }}>🕒</span>
                    <div>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        {item.searchTerm}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {item.searchType} • {new Date(item.timestamp).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {showSuggestions && (
            <>
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  ref={el => suggestionRefs.current[index] = el}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: index < suggestions.length - 1 ? '1px solid #f8f9fa' : 'none',
                    backgroundColor: selectedIndex === index ? '#f8f9fa' : 'transparent',
                    transition: 'background-color 150ms ease'
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '16px' }}>
                      {suggestion.icon}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                        {suggestion.text}
                      </div>
                      {suggestion.subtitle && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {suggestion.subtitle}
                        </div>
                      )}
                    </div>
                    {suggestion.rating && (
                      <div style={{ 
                        fontSize: '12px', 
                        color: suggestion.rating >= 4 ? '#4CAF50' : suggestion.rating >= 3 ? '#FF9800' : '#F44336',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        ⭐ {suggestion.rating.toFixed(1)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {suggestions.length === 0 && !isLoading && query.length >= 2 && (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '14px'
                }}>
                  No suggestions found for "{query}"
                </div>
              )}
            </>
          )}
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .search-box input:focus + .suggestions {
          display: block;
        }

        @media (max-width: 768px) {
          .search-box {
            font-size: 16px; /* Prevent zoom on iOS */
          }
        }
      `}</style>
    </div>
  );
};

export default SearchBox;